/* Viewport-edge clamping helpers for design-system popovers / menus.

   Two flavors:

   1. `flipPopoverHorizontally(popover, leftAlignedClass)`
      For CSS-anchored popovers (positioned `position: absolute;
      right: 0` relative to a chip's container). If the natural
      right-anchored bounds overflow the viewport's LEFT edge, the
      helper adds the `leftAlignedClass` modifier, which the
      stylesheet uses to switch the anchor to `left: 0`. Applies to:
      account menu, dashboard "New" create menu.

   2. `clampFixedMenuToViewport(menu, anchorRect)`
      For absolutely-/fixed-positioned menus that the JS itself lays
      out via `style.top` / `style.left` (i.e. no CSS anchor). The
      helper takes the menu element after it's been attached and
      positioned, then nudges it back inside the viewport if it
      overflows the right or bottom edge — flipping above the anchor
      if there isn't room below. Applies to: dashboard per-project
      action menu (Open project / Download / Duplicate / Archive). */

const VIEWPORT_INSET = 8;

export function flipPopoverHorizontally(popover: HTMLElement, leftAlignedClass: string): void {
	// Reset prior alignment so we measure the default (right-anchored)
	// state, then flip if that state overflows the viewport's edges.
	popover.classList.remove(leftAlignedClass);
	const rect = popover.getBoundingClientRect();
	const viewportW = window.innerWidth || document.documentElement.clientWidth;
	const overflowsLeft = rect.left < VIEWPORT_INSET;
	const overflowsRight = rect.right > viewportW - VIEWPORT_INSET;
	if (overflowsLeft || overflowsRight) {
		popover.classList.add(leftAlignedClass);
	}
}

export interface IAnchorRect {
	bottom: number;
	left: number;
	right: number;
	top: number;
}

export function clampFixedMenuToViewport(menu: HTMLElement, anchor: IAnchorRect): void {
	// The caller has already done a first-pass `style.top` / `style.left`
	// against the anchor — we read the menu's current bounds, then nudge
	// if they overflow. We assume the menu is `position: fixed`.
	const rect = menu.getBoundingClientRect();
	const viewportW = window.innerWidth || document.documentElement.clientWidth;
	const viewportH = window.innerHeight || document.documentElement.clientHeight;

	// Horizontal clamp — keep menu inside [inset, viewport-inset].
	let left = rect.left;
	if (rect.right > viewportW - VIEWPORT_INSET) {
		left = viewportW - VIEWPORT_INSET - rect.width;
	}
	if (left < VIEWPORT_INSET) {
		left = VIEWPORT_INSET;
	}

	// Vertical — if the menu would overflow the bottom and there's
	// more room above the anchor than below, flip above.
	let top = rect.top;
	const spaceBelow = viewportH - anchor.bottom;
	const spaceAbove = anchor.top;
	if (rect.bottom > viewportH - VIEWPORT_INSET && spaceAbove > spaceBelow) {
		top = Math.max(VIEWPORT_INSET, anchor.top - rect.height - 8);
	} else if (rect.bottom > viewportH - VIEWPORT_INSET) {
		// Bottom overflows but no room above either — just clamp.
		top = Math.max(VIEWPORT_INSET, viewportH - VIEWPORT_INSET - rect.height);
	}

	menu.style.left = `${left}px`;
	menu.style.top = `${top}px`;
}
